import React from 'react';
import styled from '@emotion/styled';
import Box from '@ff/ui/Box';
import FadeSlide from '@ff/ui/SlideShow/presets/FadeSlideShow/FadeSlide';
import SimpleSlide from '@ff/ui/SlideShow/Slide';
import { SLIDE_MIN_HEIGHT } from '@ff/ui/SlideShow/constants';
import Specs from './Specs';
import Link from '@ff/ui/Link';
import DetailSection from './DetailSection';
import Heading from './Heading';
import ButtonLink from './ButtonLink';
import type { Props as SlideContentProps } from './SlideContent';
import withSafeAreaInsetBottom from 'shared/lib/withSafeAreaInsetBottom';

export interface Props extends SlideContentProps {
  onLearnMoreClick?: (id: string | null) => void;
}

export default function SlideContentDetail(props: Props) {
  const {
    id,
    index,
    editor,
    topSubtitle,
    title,
    subtitle,
    subtitleCarousel,
    specs,
    cta,
    imageUrl,
    detail,
    highlights,
    alwaysShowDownArrow,
    enabledDownArrow,
    slideScrollSnapAnimation,
    onLearnMoreClick
  } = props;

  let Slide = FadeSlide;

  if (editor || !slideScrollSnapAnimation) {
    Slide = SimpleSlide;
  }

  const ctaButtons = (
    cta?.map((item) => (
      <ButtonLink
        key={item.text}
        href={item.url}
        disabled={item.disabled}
        appHandlerKey={item.appHandlerKey}
        width={{ _: 'auto', md: '264px' }}
      >
        {item.text}
      </ButtonLink>
    ))
  );

  const newImageUrlObj = {
    _: imageUrl._,
    md: imageUrl.md,
  }

  return (
    <Slide
      id={id}
      index={index}
      alwaysShowDownArrow={alwaysShowDownArrow}
      enabledDownArrow={enabledDownArrow}
      gradientPercent={20}
      scrollSnapAnimation={slideScrollSnapAnimation}
      imageUrl={newImageUrlObj}
      imageAlt={imageUrl.imageAlt}
    >
      <Content slideScrollSnapAnimation={slideScrollSnapAnimation}>
        <ImageContent>
          <Box marginTop='15vh'>
            <Heading topSubtitle={topSubtitle} title={title} subtitle={subtitle} subtitleCarousel={subtitleCarousel} />
          </Box>
          {/* filler Box to expand top and bottom contents */}
          <Box flex='1'></Box>
          <SpecsAndButtonContainer>
            {/* ------- specs for desktop ----------- */}
            <Box display={{ _: 'none', md: 'flex' }} justifyContent='center' alignItems='center'>
              <Specs specs={specs} />
              <ButtonContainer>
                {ctaButtons}
              </ButtonContainer>
            </Box>
          </SpecsAndButtonContainer>
        </ImageContent>
        <DetailContainer>
          {/* ------- specs for mobile ----------- */}
          <Box display={{ _: 'block', md: 'none' }}>
            <Specs specs={specs} />
          </Box>
          <DetailSection {...detail} />
          <StyledLinkContainer>
            <StyledLink
              tabEnter
              fontSizeDesktop={highlights?.learnMore?.learnMoreFontSizeDesktop}
              fontSizeMobile={highlights?.learnMore?.learnMoreFontSizeMobile}
              variant='primary'
              onClick={() => {
                if (highlights?.slides?.length > 0) {
                  onLearnMoreClick?.(id)
                }
              }}
            >
              {highlights?.learnMore?.learnMoreText}
            </StyledLink>
          </StyledLinkContainer>
        </DetailContainer>
      </Content>
    </Slide>
  );
}

const Content = styled(Box)<{ slideScrollSnapAnimation?: boolean }>(({ slideScrollSnapAnimation }) => ({
  display: 'flex',
  width: '100%',
  height: slideScrollSnapAnimation ? 'var(--doc-height)' : 'auto',
  minHeight: `max(${SLIDE_MIN_HEIGHT}px, 100vh)`,
  flexDirection: 'column',
  /* items going over the width would cause flicking in android */
  maxWidth: '100vw',
  overflow: 'hidden',
  /* items going over the width would cause flicking in android */
  position: 'relative',
}));

const StyledLinkContainer = styled(Box)({
  marginBottom: '0px',
  marginLeft: 'auto',
  marginRight: 'auto',
  marginTop: '16px',
  width: 'min(1140px, 98%)',
  textAlign: 'end',
});

const StyledLink = styled(Link)<{ fontSizeDesktop: string, fontSizeMobile: string }>(({ theme, fontSizeDesktop, fontSizeMobile }) => ({
  fontSize: fontSizeMobile ? `${fontSizeMobile}px` : '14px',
  lineHeight: '26px',
  color: theme.colors.white,
  textDecoration: 'underline',
  [theme.mediaQueries.md]: {
    fontSize: fontSizeDesktop ? `${fontSizeDesktop}px` : '16px',
  },
}));

const SpecsAndButtonContainer = styled(Box)({
  minHeight: '122px',
  background: 'linear-gradient(0deg, #0C0C0C 1%, rgba(12, 12, 12, 0) 100%)'
});

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'none',

  [theme.mediaQueries.md]: {
    display: 'flex',
    margin: '50px'
  },
}));

const DetailContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: '#0C0C0C',
  paddingTop: '0px',
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingBottom: withSafeAreaInsetBottom('60px'),

  [theme.mediaQueries.md]: {
    paddingTop: '50px',
    paddingLeft: '50px',
    paddingRight: '50px',
    paddingBottom: '50px',
  }
}));

const ImageContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flex: 1,
});
